<template>
    <div class="page">
      <v-container fluid>
        <v-form ref="form">
          <v-row class="d-flex justify-center mt-3">
            <v-col cols="2">
              <v-text-field
                label="CPF"
                v-model="form.filters.cpf"
                v-mask="'###.###.###-##'"
                outlined
                dense
                @paste.prevent="validarPegarNumeros"
              ></v-text-field>
            </v-col>
            <v-col cols="3">
              <v-text-field
                label="Nome do Usuário"
                v-model="form.filters.nomeCompleto"
                outlined
                dense
                maxlength="252"
              ></v-text-field>
            </v-col>
            <v-col cols="3">
              <v-autocomplete
                label="Perfil"
                :items="form.lists.listPerfis"
                item-text="nome"
                item-value="id"
                v-model="form.filters.perfil"
                outlined
                dense
              ></v-autocomplete>
            </v-col>
            <sigfoco-select-ativo
              :cols="2"
              v-model="form.filters.situacao"
              labelType="Situação"
            ></sigfoco-select-ativo>
          </v-row>
          <div class="text-center">
            <v-col cols="12">
              <sigfoco-tooltip
                text="Buscar"
                icon="mdi-magnify"
                @click="buscarUsuarios"
              ></sigfoco-tooltip>
              <sigfoco-tooltip
                text="Limpar"
                icon="mdi-eraser"
                @click="resetForm"
              ></sigfoco-tooltip>
              <sigfoco-tooltip
              :text="'Incluir'"
              :icon="'mdi-plus'"
              @click="openDialogManterUsuario()"
            ></sigfoco-tooltip>
            </v-col>
          </div>
        </v-form>
        <v-data-table
          class="mt-5"
          :headers="headers"
          :items="form.lists.listUsuarios"
          item-key="id"
          :footer-props="$footerProps"
          sort-by="nome"
          show-expand
          :expanded.sync="expanded"
          :items-per-page="itemsPerPage"
          :page.sync="paginaAtual"
          :server-items-length="totalItens"
          @update:page="avancarItemsPerPage"
          @update:items-per-page="atualizarItemsPerPage"
        >
          <template v-slot:[`item.cpf`]="{ item }">
            <div class="hide-report">
              {{ $esconderCpf(item.cpf) }}
            </div>
          </template>
          <template v-slot:[`item.nomeCompleto`]="{ item }">
            {{ item.nomeSocial ?? item.nomeCompleto }}
          </template>
          <template v-slot:[`item.email`]="{ item }">
            {{ item.email }}
          </template>
          <template v-slot:[`item.perfis`]="{ item }">
            {{ item.perfis }}
          </template>
          <template v-slot:[`item.situacao`]="{ item }">
            <sigfoco-label-ativo-inativo :ativo="item.situacao"></sigfoco-label-ativo-inativo>
          </template>
          <template v-slot:[`item.acoes`]="{ item }">
            <sigfoco-tooltip
              text="Visualizar"
              icon="mdi-eye-outline"
              small
              @click="openDialogVisualizarUsuario(item)"
            ></sigfoco-tooltip>
            <sigfoco-tooltip
              text="Editar"
              icon="mdi-pencil"
              small
              @click="openDialogManterUsuario(item)"
            ></sigfoco-tooltip>
            <sigfoco-tooltip
              :text="`${item.situacao ? 'Inativar' : 'Ativar'}`"
              :icon="`${item.situacao ? 'mdi-toggle-switch' : 'mdi-toggle-switch-off'}`"
              :color="`${item.situacao ? 'var(--primary-button-color)' : 'silver'}`"
              small
              @click="inativarAtivarUsuario(item)"
            ></sigfoco-tooltip>
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td class="perfis" :colspan="headers.length">
              <v-chip-group active-class="primary--text" column>
                <v-chip
                  class="chip-permissao"
                  v-for="(perfil, i) in item.perfis"
                  :key="i"
                >
                  <v-icon class="perfil" small>mdi-account-multiple</v-icon>
                  {{ perfil.nome }}
                </v-chip>
              </v-chip-group>
            </td>
          </template>
          <template v-slot:[`item.emUso`]="{ item }">
          <sigfoco-tooltip
            :text="'Detalhar Uso'"
            :icon="item.emUso ? 'mdi-check-circle-outline' : 'mdi-close-circle-outline'"
            :color="item.emUso ? 'green' : 'red'"
            :small="true"
            :disabled="!item.emUso"
            @click="item.emUso ? openDialogDetalharUsoUsuario(item) : null"
          ></sigfoco-tooltip>
        </template>
        </v-data-table>
      </v-container>
      <EditarUsuarioDialog
        :visible="dialogManterUsuario.visible"
        :content="dialogManterUsuario.content"
        :edit="dialogManterUsuario.edit"
        @onClose="dialogManterUsuario.close()"
        @onSave="buscarUsuarios()"
      ></EditarUsuarioDialog>
      <VisualizarUsuarioDialog
        :visible="dialogVisualizarUsuario.visible"
        :content="dialogVisualizarUsuario.content"
        @onClose="dialogVisualizarUsuario.close()"
      ></VisualizarUsuarioDialog>
      <DetalharUsoUsuarioDialog
      :visible="dialogDetalharUsoUsuario.visible"
      :content="dialogDetalharUsoUsuario.content"
      @onClose="dialogDetalharUsoUsuario.close()"
      ></DetalharUsoUsuarioDialog>
    </div>
  </template>
  
  <script>
  import { Dialog } from "~/commons/classes/dialog";
  import pageLoadingMixin from "mixins/page-loading.mixin";
  import { Usuario } from "~/commons/classes/usuario";
  import ManterUsuarioService from '~/services/manter-usuario.service';
  import ManterPerfilService from '~/services/manter-perfil.service';
  import AuthService from '~/services/auth.service';
  import EditarUsuarioDialog from "./dialogs/EditarUsuarioDialog.vue";
  import VisualizarUsuarioDialog from "./dialogs/VisualizarUsuarioDialog.vue";
  import DetalharUsoUsuarioDialog from "./dialogs/DetalharUsoUsuarioDialog.vue";
  import paginationMixin from '~/mixins/paginacao-datatable.mixin';
  
  export default {
    name: "ManterUsuarioView",
    mixins: [ManterUsuarioService, AuthService, pageLoadingMixin, ManterPerfilService, paginationMixin],
    components: {
      EditarUsuarioDialog,
      VisualizarUsuarioDialog,
      DetalharUsoUsuarioDialog,
    },
    data: () => ({
      dialogManterUsuario: Dialog.Build(),
      dialogVisualizarUsuario: Dialog.Build(),
      dialogDetalharUsoUsuario: Dialog.Build(),
      form: {
        filters: {
          nomeCompleto: null,
          situacao: null,
          cpf: null,
          perfis: null,
        },
        lists: {
          listGrupos: [],
          listPerfis: [],
          listUsuarios: []
        }
      },
      headers: [
        { text: "CPF", align: "center", value: "cpf" },
        { text: "Nome do Usuário", align: "center", value: "nomeCompleto" },
        { text: "E-mail", align: "center", value: "email" },
        { text: "Perfil", align: "center", value: "data-table-expand" },
        { text: "Situação", align: "center", value: "situacao" },
        { text: "Ações", align: "center", value: "acoes", sortable: false },
        { text: "Em uso", align: "center", value: "emUso", sortable: false },
      ],
    }),
    methods: {
      validarPegarNumeros(event) {
        const textoPuro = event.clipboardData.getData('Text').replace(/\D/g, '');
        this.form.filters.cpf = textoPuro;
      },
      openDialogManterUsuario(usuario) {
        usuario
        ? this.$visualizarUsuario(usuario).then(({ data }) => {
            this.dialogManterUsuario.open(new Usuario(data.usuario), true);
          })
        : this.dialogManterUsuario.open(Usuario.Build());
      },
      openDialogVisualizarUsuario(usuario) {
        this.$visualizarUsuario(usuario).then(
          ({ data }) => (this.dialogVisualizarUsuario.open(new Usuario(data.usuario)))
        );
      },
      openDialogDetalharUsoUsuario(usuario) {
        this.$detalharUsoUsuario(usuario).then(
          ({ data }) => (this.dialogDetalharUsoUsuario.open(data.resposta))
        );
      },
      buscarUsuarios() {
        const filters = Object.entries(this.form.filters).reduce((acumulador, [key, value]) => {
          if (key === 'cpf' && value) {
            acumulador[key] = this.$unMask(value);
          } else if (value !== null && value !== undefined)  {
            acumulador[key] = value;
          }
          return acumulador;
        }, {});
        this.$buscarUsuario({
          params: {
            ...filters,
            pagina: this.paginaAtual,
            tamanhoPagina: this.itemsPerPage,
          }
        }).then(({ data }) => {
          this.form.lists.listUsuarios = data.lista;
          this.totalItens = data.totalItens;
          this.totalPaginas = data.totalPaginas;
        });
      },
      inativarAtivarUsuario(usuario) {
        this.$swal({
          text: `Deseja confirmar a ${usuario.situacao ? "inativação" : "ativação"} do Usuário?`,
          type: "warning",
          showCancelButton: true,
          confirmButtonText: `Confirmar`,
          cancelButtonText: `Cancelar`,
        }).then(() => {
          if(usuario.emUso) {
            this.mostrarModalEmUso();
          } else {
            this.$inativarAtivarUsuario(
            { id: usuario.id },
            usuario.id
          ).then(({ data }) => {
            this.$notifySuccess(data.mensagem);
            this.buscarUsuarios();
          });
          }
        });
      },
      excluirUsuario(usuario) {
        this.$swal({
          text: `Deseja confirmar a exclusão do usuário?`,
          type: "error",
          showCancelButton: true,
          confirmButtonText: `Confirmar`,
          cancelButtonText: `Cancelar`,
        }).then(() => {
          if(usuario.emUso){
            this.mostrarModalEmUso();
          } else {
              this.$excluirUsuario(
              { id: usuario.id },
              usuario.id
            ).then(({ data }) => {
              this.$notifySuccess(data.mensagem);
              this.buscarUsuarios();
            });
          }
        });
      },
      resetForm() {
        this.form.filters.cpf = '';
        this.$nextTick(() => {
          this.form.filters = {
            nomeCompleto: null,
            situacao: null,
            cpf: null,
            perfis: null,
          };
          this.form.lists.listUsuarios = [];
          this.expanded = [];
          this.resetPagination();
        });
      },
      buscarItens() {
        this.buscarUsuarios();
      },
      
      mostrarModalEmUso() {
        this.$swal({
        text: "O registro não pode ser inativado ou excluído, pois está em uso em outra(s) funcionalidade(s).",
        type: "warning",
        confirmButtonText: `OK`,
        });
      },
    },
    mounted() {
      this.$buscarPerfis({ params: { situacao: true } }).then(({ data }) => {
        this.form.lists.listPerfis = data.lista;
      });
    }
  };
  </script>
  
  <style scoped>
  .permissoes {
    padding: 1em !important;
    text-align: center;
    font-weight: 500;
  }
  .permissao {
    padding-right: 0.5em;
  }
  </style>